import React, { useEffect } from 'react';
import { Flex, Text } from '@indeed/ifl-components';
import { Information } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import {
    useGetScreenerQuestionLabelDataQuery,
    useGetScreenerQuestionLabelDataWithDqQuery,
} from './__generated__/ScreenerQuestionLabel.generated';
import { isDqPrefillEnabled } from '../../../lib/proctor/isDqPrefillEnabled';

interface ScreenerQuestionLabelProps {
    applicationDraftId: string;
    onLabelChange?: (applicationDraftId: string, label: string | null) => void;
}

/**
 * Tells the seeker which autofilled question sections a draft carries, so they
 * know what to look for before submitting:
 *
 * | screener (SQ) | demographic (DQ) | label                                                        |
 * |---------------|------------------|--------------------------------------------------------------|
 * | yes           | yes              | "Employer and voluntary self-identification questions included" |
 * | yes           | no               | "Employer questions included"                                |
 * | no            | yes              | "Voluntary self-identification questions included"           |
 * | no            | no               | nothing (or the "may be included" fallback on query error)   |
 */
export const ScreenerQuestionLabel: React.FC<ScreenerQuestionLabelProps> = ({
    applicationDraftId,
    onLabelChange,
}) => {
    const gt = GettextFactory();
    const { getGroup } = useProctorGroups();
    const dqPrefillEnabled = isDqPrefillEnabled(getGroup);
    const employerLabel = gt.gettext('Employer questions included');
    const demographicLabel = gt.gettext('Voluntary self-identification questions included');
    const combinedLabel = gt.gettext(
        'Employer and voluntary self-identification questions included',
    );
    const fallbackLabel = gt.gettext('Employer questions may be included');
    const baseQueryInput = {
        variables: { applicationDraftId },
        fetchPolicy: 'cache-first' as const,
        errorPolicy: 'all' as const,
        skip: dqPrefillEnabled,
    };
    const baseQuery = useGetScreenerQuestionLabelDataQuery(baseQueryInput);
    const dqQuery = useGetScreenerQuestionLabelDataWithDqQuery({
        ...baseQueryInput,
        skip: !dqPrefillEnabled,
    });
    const { data, loading, error } = dqPrefillEnabled ? dqQuery : baseQuery;
    const draft = data?.findApplicationDrafts?.operationDetails?.find(
        (candidate) => candidate.applicationDraftId === applicationDraftId,
    );
    const dqDraft = dqQuery.data?.findApplicationDrafts?.operationDetails?.find(
        (candidate) => candidate.applicationDraftId === applicationDraftId,
    );
    const hasScreenerQuestions = (draft?.questionsAndAnswers?.screener?.length ?? 0) > 0;
    const hasDemographicQuestions =
        dqPrefillEnabled && (dqDraft?.questionsAndAnswers?.demographic?.length ?? 0) > 0;

    let label: string | null;
    if (hasScreenerQuestions && hasDemographicQuestions) {
        label = combinedLabel;
    } else if (hasScreenerQuestions) {
        label = employerLabel;
    } else if (hasDemographicQuestions) {
        label = demographicLabel;
    } else {
        label = error ? fallbackLabel : null;
    }

    useEffect(() => {
        if (!loading) {
            onLabelChange?.(applicationDraftId, label);
        }
    }, [applicationDraftId, label, loading, onLabelChange]);

    if (!label) {
        return null;
    }

    return (
        <Flex
            aria-hidden={onLabelChange ? true : undefined}
            sx={{
                alignItems: 'flex-start',
                gap: 1,
                paddingBlockStart: 2,
            }}
        >
            <Information
                size="xs"
                variant="stroke"
                color="foreground.weak"
                aria-hidden
                sx={{ flexShrink: 0 }}
            />
            <Text level={1} variant="secondary" sx={{ fontWeight: 'normal' }}>
                {label}
            </Text>
        </Flex>
    );
};
